import React, { createContext, useCallback, useContext, useMemo } from 'react';
import resources, { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '../i18n/resources';
import useLanguagePreference from '../hooks/useLanguagePreference';

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  availableLanguages: SUPPORTED_LANGUAGES,
  setLanguage: () => {},
  translate: () => undefined,
});

const getFromPath = (source, path) => {
  if (!source || !path) {
    return undefined;
  }

  const segments = path.split('.');
  let current = source;

  for (const segment of segments) {
    if (
      current
      && typeof current === 'object'
      && Object.prototype.hasOwnProperty.call(current, segment)
    ) {
      current = current[segment];
    } else {
      return undefined;
    }
  }

  return current;
};

export const LanguageProvider = ({ children }) => {
  const { language: storedLanguage, setLanguage: setStoredLanguage } = useLanguagePreference();

  const language = useMemo(() => {
    if (SUPPORTED_LANGUAGES.includes(storedLanguage)) {
      return storedLanguage;
    }
    return DEFAULT_LANGUAGE;
  }, [storedLanguage]);

  const translateFromNamespace = useCallback(
    (namespace, key) => {
      if (!namespace || !key) {
        return undefined;
      }

      const languageResources = resources[language];
      if (!languageResources) {
        return undefined;
      }

      const namespaceResources = languageResources[namespace];
      if (!namespaceResources) {
        return undefined;
      }

      return getFromPath(namespaceResources, key);
    },
    [language],
  );

  const translate = useCallback(
    (namespaces, key) => {
      if (!key) {
        return undefined;
      }

      const namespaceList = Array.isArray(namespaces) ? namespaces : [namespaces];

      for (const namespace of namespaceList) {
        const directResult = translateFromNamespace(namespace, key);
        if (directResult !== undefined && directResult !== null) {
          return directResult;
        }

        if (typeof key === 'string') {
          if (key.startsWith(`${namespace}.`)) {
            const trimmedKey = key.slice(namespace.length + 1);
            const trimmedResult = translateFromNamespace(namespace, trimmedKey);
            if (trimmedResult !== undefined && trimmedResult !== null) {
              return trimmedResult;
            }
          }

          if (key.startsWith(`${namespace}:`)) {
            const trimmedKey = key.slice(namespace.length + 1);
            const trimmedResult = translateFromNamespace(namespace, trimmedKey);
            if (trimmedResult !== undefined && trimmedResult !== null) {
              return trimmedResult;
            }
          }
        }
      }

      return undefined;
    },
    [translateFromNamespace],
  );

  const value = useMemo(
    () => ({
      language,
      availableLanguages: SUPPORTED_LANGUAGES,
      setLanguage: setStoredLanguage,
      translate,
    }),
    [language, setStoredLanguage, translate],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
